<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { forgotPassword } from '@/api/auth'
import { useAuthContext } from '@/composables/useAuthContext'
import AppFooter from '@/components/layout/AppFooter.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'

const { t } = useI18n()
const { loginRoute } = useAuthContext()

const email = ref('')
const loading = ref(false)
const sent = ref(false)
const error = ref<string | null>(null)
const fieldErrors = ref<Record<string, string[]>>({})

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

async function handleSubmit(): Promise<void> {
  fieldErrors.value = {}
  error.value = null
  loading.value = true
  try {
    await forgotPassword({ email: email.value })
    sent.value = true
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
      fieldErrors.value = e.response.data.errors
    } else {
      error.value = t('common.error_generic')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-slate-900 flex flex-col">
    <div class="flex-1 flex items-center justify-center px-4 py-12">
    <div class="w-full max-w-md">
      <!-- Wordmark -->
      <div class="text-center mb-8">
        <a href="/" class="inline-flex items-center gap-2 group">
          <span class="text-2xl font-black text-white tracking-tight">Startline</span>
          <span class="w-2 h-2 rounded-full bg-emerald-400 mb-0.5" />
        </a>
      </div>

      <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
        <h1 class="text-2xl font-bold text-white mb-2">{{ t('auth.forgot_password.heading') }}</h1>
        <p class="text-slate-400 text-sm mb-6">{{ t('auth.forgot_password.instructions') }}</p>

        <!-- Success state -->
        <div v-if="sent" class="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-4 text-sm text-emerald-400">
          {{ t('auth.forgot_password.sent') }}
        </div>

        <!-- Form -->
        <form v-else novalidate @submit.prevent="handleSubmit" class="space-y-5">
          <div v-if="error" class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
            {{ error }}
          </div>

          <div>
            <label for="forgot-email" class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('auth.forgot_password.email_label') }}
            </label>
            <input
              id="forgot-email"
              v-model="email"
              type="email"
              autocomplete="email"
              class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="fieldErrors.email ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            />
            <p v-if="fieldErrors.email" class="mt-1 text-xs text-red-400">{{ fieldErrors.email[0] }}</p>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
          >
            <AppSpinner v-if="loading" size="button" />
            {{ loading ? t('common.loading') : t('auth.forgot_password.submit') }}
          </button>
        </form>

        <p class="mt-6 text-sm text-center">
          <RouterLink :to="loginRoute" class="text-slate-400 hover:text-slate-300 transition-colors">
            ← {{ t('auth.forgot_password.back_to_login') }}
          </RouterLink>
        </p>
      </div>
    </div>
    </div>
    <AppFooter />
  </div>
</template>
